import { Alert } from "@mui/material";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  AdminListRequestOfDescendantSettingFilter,
  DescendantSettingFilter,
  ListResponseOfDescendantSettingCache,
  SettingCache,
} from "services/KnowfullyAdminClient";
import useGlobalContext from "hooks/globalData";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import SettingValue from "./SettingValue";

type DescendantSettingsTableProps = {
  setting: SettingCache;
};

const DescendantSettingsTable = ({ setting }: DescendantSettingsTableProps) => {
  const { formatMessage: t } = useIntl();
  const { settingService } = useGlobalContext();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("organization.name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const settingFilter = DescendantSettingFilter.fromJS({
    organizationId: setting.organizationId,
    settingId: setting.settingId,
  });

  const filter = AdminListRequestOfDescendantSettingFilter.fromJS({
    pageNumber: page,
    pageSize: rowsPerPage,
    parameters: settingFilter,
    sortField: orderBy,
    sortDescending: order === "desc",
  });

  const fetchSettings = () => settingService.getDescendantSettings(filter);

  const {
    isError,
    data,
    error,
    isFetching,
  } = useQuery<ListResponseOfDescendantSettingCache>(
    [
      "settings",
      setting.organizationId,
      order,
      orderBy,
      page,
      rowsPerPage,
      setting.settingId,
    ],
    fetchSettings,
    {
      keepPreviousData: true,
    }
  );

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const cells: Array<TableCellType> = [
    {
      accessor: "organizationName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "childOrganization" }),
      customSortAccessor: "organization.name",
    },
    {
      accessor: "value",
      align: "left",
      width: 0,
      type: "node",
      label: t({ id: "valueLabel" }),
      View: SettingValue,
    },
    {
      accessor: "inheritedOrganizationName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "settingInheritedFromLabel" }),
      noSort: true,
    },
  ];

  const rawSummary = (data?.data ?? []).map(x => ({id: x.organizationName!, ...x}));

  return (
    <LazyTable
      {...{
        rowCount: data?.totalRecords ?? 0,
        rows: rawSummary ?? [],
        loading: isFetching,
        dense: true,
        cells,
        page,
        setPage,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        rowsPerPage,
        setRowsPerPage,
        collapsable: false,
        allowDragAndDrop: false,
      }}
    />
  );
};

export default DescendantSettingsTable;
